'use client';

import { useEffect, useState } from 'react';
import { masterTaskList } from '../mockData/auditData';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import {
  AlertCircle,
  CheckCircle2,
  MinusCircle,
  TriangleAlert,
  CalendarClock,
  FileClock,
  Check,
} from 'lucide-react';

import firebase from 'firebase/compat/app';
import { useAppSelector } from 'hooks';
import useQueryParams from 'hooks/useQueryParams';
import { base64Decode } from 'Views/AuditReportsRegistrations';
import { format } from 'date-fns';
import { AuditContent } from '../../InternalAuditOverview/components/AuditMatrix';

export type AuditSubTaskAttachements = {
  download: string;
  fileId: string;
  fileName: string;
  pathRef: string;
  type: string;
};

export type SubTask = {
  id: string;
  label: string;
  description?: string;
  status: 'not-audited' | 'passed' | 'observation' | 'deviation';
  taskId: string;
  attachments?: AuditSubTaskAttachements[];
  responsible?: {
    id: string;
    displayName: string;
  };
  dueDate?: firebase.firestore.Timestamp;
};

export type Task = {
  id: string;
  label: string;
  subTaskIds: string[];
  category: string;
  order: number;
};

export interface InternalAudit {
  id: string;
  auditor: {
    userId: string;
    displayName: string;
  };
  createTime: firebase.firestore.Timestamp;
  completeTime?: firebase.firestore.Timestamp;
  groupId: string | null;
  location: {
    id: string;
    title: string;
  };
  state: 'DRAFT' | 'COMPLETED';
  subGroup: {
    id: string;
    title: string;
  } | null;
  subTasksMap: {
    [taskId: string]: SubTask;
  };
  tasksMap: {
    [taskId: string]: Task;
  };
}

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'passed':
      return 'bg-green-100 hover:bg-green-200 text-green-600';
    case 'observation':
      return 'bg-orange-100 hover:bg-orange-200 text-orange-600';
    case 'deviation':
    case 'deviation-rejected':
      return 'bg-red-100 hover:bg-red-200 text-red-600';
    case 'deviation-onhold':
      return 'bg-yellow-100 hover:bg-yellow-200 text-yellow-600';
    case 'deviation-pending':
      return 'bg-yellow-100 hover:bg-yellow-200 text-yellow-600';
    case 'deviation-resolved':
      return 'bg-green-300 hover:bg-green-200 text-green-800';
    case 'not-audited':
      return 'bg-gray-100 hover:bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 hover:bg-gray-100 text-gray-600';
  }
};

const fileObj = {
  type: 'image/png',
  download: 'https://placehold.co/400x400/png',
};

export const getStatusIcon = (status: string) => {
  console.log({ status });

  switch (status) {
    case 'passed':
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    case 'observation':
      return <TriangleAlert className="w-4 h-4 text-orange-600" />;
    case 'observation-completed':
      return <Check className="w-4 h-4 text-green-800" />;
    case 'deviation':
    case 'deviation-rejected':
      return <AlertCircle className="w-4 h-4 text-red-600" />;
    case 'deviation-onhold':
      return <CalendarClock className="w-4 h-4 text-yellow-600" />;
    case 'deviation-pending':
      return <FileClock className="w-4 h-4 text-yellow-600" />;
    case 'deviation-resolved':
      return <AlertCircle className="w-4 h-4 text-green-800" />;
    case 'not-audited':
      return <MinusCircle className="w-4 h-4 text-gray-600" />;
    default:
      return <MinusCircle className="w-4 h-4 text-gray-600" />;
  }
};

export function AuditMatrix() {
  const userId = useAppSelector((state) => state.user.details.uid);
  const orgsMap = useAppSelector((state) => state.orgs.orgMap);
  const [audits, setAudits] = useState<any[]>([]);
  const query = useQueryParams();
  const q = query.get('q') as string;
  const decodedData = base64Decode(q);

  const parsedData = JSON.parse(decodedData) as {
    groupIds: string[];
    groupTitle: string;
    categories: string[];
    orgIds: string[];
    dateInterval: [string, string];
    completedTasks: boolean;
    incompleteTasks: boolean;
    // useSubGroup: boolean;
    dateType: 'time' | 'date' | 'week' | 'month' | 'quarter' | 'year';
  };
  console.log({ parsedData });

  // const [selectedCell, setSelectedCell] = useState<{
  //   taskId: string;
  //   locationId: string;
  // } | null>(null);

  const [selecteAuditItem, setSelectedAuditItem] = useState<any | null>(null);
  const [filterCategories, setFilterCategories] = useState<string[]>(parsedData.categories || []);
  const currentAudit = audits.find((audit) => audit.id === selecteAuditItem);
  useEffect(() => {
    const unsubscribe = firebase
      .firestore()
      .collection(`internalAudits`)
      // .orderBy('createTime', 'desc')
      .where('auditor.userId', '==', userId)
      .where('type', '==', 'external')
      .where('state', '==', 'COMPLETED')
      .orderBy('completeTime', 'asc')
      .onSnapshot(
        (query) => {
          if (!query.empty) {
            const list: any[] = query.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            setAudits(list);
          }
        },
        (error) => {
          console.error('Error fetching audits:', error);
        }
      );
    return () => unsubscribe?.();
  }, [userId]);
  const filteredMasterList = masterTaskList.filter((t) =>
    filterCategories.length > 1 ? filterCategories.includes(t.category.id) : true
  );
  const matrix = buildLatestTaskMatrix(audits, filteredMasterList);

  const locations = parsedData.orgIds.map((orgId) => ({
    id: orgId,
    // use subgroup first if exists
    title: orgsMap[orgId]?.subGroup?.title || orgsMap[orgId]?.title,
  }));

  console.log({ locations });

  return (
    <div className="overflow-x-auto">
      <div className="min-w-max">
        {/* Header row with location names */}
        <div className="grid grid-cols-[250px_repeat(25,180px)] gap-1 mb-1">
          <div className="font-bold p-2">Opgave</div>
          {locations.map((location) => {
            const latestAuditForLocation = audits.find(
              (audit) => audit.location.id === location.id && audit.completeTime
            );
            const latestAuditDate = latestAuditForLocation?.completeTime?.toDate();
            return (
              <div className="flex flex-col">
                <div key={location.id} className="font-bold px-2 text-sm">
                  {location.title}
                </div>
                {latestAuditDate && (
                  <div className="px-2 text-xs text-neutral-500">
                    {format(latestAuditDate, 'dd/MM/yyyy')}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Task rows */}
        {filteredMasterList.map((task) => (
          <div key={task.id} className="grid grid-cols-[250px_repeat(25,180px)] gap-1 mb-1">
            <div className="p-2 bg-gray-50 sticky left-0">
              {task.label}
              <div className="text-xs text-neutral-500">({task.category.label})</div>
            </div>
            {locations.map((location) => {
              const cellData = matrix[task.id]?.[location.id];
              console.log({ cellData, task });

              const status = getStatusByImportance(cellData?.status);
              const statusColor = getStatusColor(status || 'not-audited');
              const hasCompleted = false;
              // cellData?.followUpTasks?.length > 0 &&
              // cellData?.followUpTasks.every((t) => t.completed);

              const hasStatuses = cellData?.status?.filter(
                (status) => status !== 'not-audited' || !status
              );

              const noStatus = !hasStatuses || hasStatuses.length < 1;

              const getIcons = !noStatus
                ? hasStatuses.map((status) => getStatusIcon(status))
                : getStatusIcon('not-audited');

              return (
                <Dialog key={location.id}>
                  <DialogTrigger asChild disabled={noStatus}>
                    <button
                      className={`p-4 h-16 flex items-center justify-center transition-colors ${
                        noStatus ? 'cursor-not-allowed' : ''
                      } ${statusColor} ${
                        hasCompleted &&
                        `${getStatusColor('deviation-resolved')} hover:!bg-green-200`
                      }`}
                      onClick={() => setSelectedAuditItem(cellData.auditId)}
                    >
                      <div className="flex gap-1">{getIcons}</div>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="overflow-y-auto max-h-[calc(100vh-100px)]">
                    <DialogHeader>
                      <DialogTitle>{location.title}</DialogTitle>
                    </DialogHeader>
                    <AuditContent audit={currentAudit} taskId={task.id} />
                  </DialogContent>
                </Dialog>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// function AuditContent({ audit, taskId }: { audit: InternalAudit; taskId: string }) {
//   const { t } = useTranslation();
//   const tmpId = taskId.replace('task', '');
//   const task = audit.tasksMap[taskId];
//   const subTasks = Object.entries(audit.subTasksMap)
//     .filter(([_, subTask]) => subTask.taskId === taskId)
//     .sort((a, b) => a[1].label.localeCompare(b[1].label))
//     .map(([_, subTask]) => subTask);
//   console.log({ audit, tmpId, taskId, subTasks });

//   return (
//     <div className="space-y-4">
//       <h3>{task.label}</h3>
//       {subTasks.map((subTask) => {
//         // @ts-ignore
//         const deviationState = audit?.deviationState?.[subTask.id];
//         // @ts-ignore
//         const hasDeviationStatus = deviationState?.status;
//         // @ts-ignore
//         const auditId = deviationState?.auditId;
//         // @ts-ignore
//         const ref = audit.deviationRefs?.find((d) => d.id === auditId);

//         const status = hasDeviationStatus || subTask.status;
//         const colorCls = getStatusColor(status);
//         const icon = getStatusIcon(status);

//         // hack to remove "-rejected" from status to display it similar to deviation
//         const statusWithoutRejected = status.replace('-rejected', '');
//         const statusLabel = statusList?.find((s) => s.value === statusWithoutRejected)?.label;

//         const headerStatusColor = colorCls.replace(/\bhover:[^\s]+/g, '');
//         const deviationCreateTime = audit.completeTime?.toDate();
//         const deviationPendingTime = deviationState?.completeTime?.toDate();
//         return (
//           <>
//             <Card className={`shadow-none`}>
//               <CardHeader className={`rounded-t-xl ${headerStatusColor}`}>
//                 <div className="flex flex-col gap-2">
//                   <div className="flex items-center justify-between">
//                     <Badge className={colorCls + ' shadow-none p-0'}>
//                       {icon} <span className="ml-1">{statusLabel}</span>
//                     </Badge>
//                     <div className="flex gap-2">
//                       {status === 'deviation-pending' && (
//                         <ApproveActions auditId={audit.id} subTaskId={subTask.id} />
//                       )}
//                       <Tooltip>
//                         <TooltipTrigger asChild>
//                           {ref?.parent?.parent?.path && auditId && (
//                             <Button
//                               size="icon"
//                               variant="outline"
//                               onClick={() =>
//                                 window.open(
//                                   // gives orgs/[orgId]
//                                   `${ref.parent.parent.path}/audit/audits/${auditId}`,
//                                   '_blank'
//                                 )
//                               }
//                             >
//                               <ExternalLink className="h-4 w-4" />
//                             </Button>
//                           )}
//                         </TooltipTrigger>
//                         <TooltipContent>Gå til opgaven</TooltipContent>
//                       </Tooltip>
//                     </div>
//                   </div>
//                   {subTask.label}

//                   <div className="text-xs">
//                     Oprettet:{' '}
//                     {t('date.exactFormat', {
//                       dayOfWeek: deviationCreateTime,
//                       dayOfMonth: deviationCreateTime,
//                       month: deviationCreateTime,
//                       time: deviationCreateTime,
//                     })}
//                   </div>
//                   {/* <div className="text-xs">
//                     Afventer godkendelse siden:{' '}
//                     {t('date.exactFormat', {
//                       dayOfWeek: deviationPendingTime,
//                       dayOfMonth: deviationPendingTime,
//                       month: deviationPendingTime,
//                       time: deviationPendingTime,
//                     })}
//                   </div> */}

//                   {/* <div className="flex justify-end items-end">
//                     {status === 'observation' && (
//                       <div className="flex items-center space-x-2">
//                         <Checkbox id="terms" />
//                         <label
//                           htmlFor="terms"
//                           className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
//                         >
//                           Kvitter for denne er set
//                         </label>
//                       </div>
//                     )}
//                   </div> */}
//                 </div>
//               </CardHeader>
//               {status && (status !== 'not-audited' || status !== 'passed') && (
//                 <CardContent className="pt-2">
//                   <div className="flex flex-col gap-2">
//                     <div className="flex items-center justify-between">{subTask.description}</div>
//                     <div className="flex gap-1 items-center">
//                       {subTask.attachments && (
//                         <RenderFiles
//                           size="sm"
//                           attachments={subTask.attachments}
//                           // removeFile={() => {}}
//                           disabled={false}
//                         />
//                       )}
//                     </div>
//                     <AuditTimeline orgId={ref?.parent?.parent?.id} subTaskId={subTask.id} />
//                   </div>
//                 </CardContent>
//               )}
//             </Card>
//           </>
//         );
//       })}
//     </div>
//   );
// }

// function AuditTimeline({ orgId, subTaskId }: { orgId: string; subTaskId: string }) {
//   const { t } = useTranslation();
//   const [expanded, setExpanded] = useState(false);
//   console.log({ orgId, subTaskId });
//   const activity = useActivity({ orgId, subTaskId });
//   const hasActivity = activity.length > 0;
//   if (!hasActivity) return null;
//   return (
//     <div className="flex flex-col">
//       <Button size="sm" variant="ghost" onClick={() => setExpanded((o) => !o)}>
//         <ChevronDown
//           className={`transform transition-transform duration-200 text-gray-400 ${
//             expanded ? 'rotate-180' : 'rotate-0'
//           }`}
//         />
//       </Button>

//       <div
//         className={`overflow-hidden  transition-[max-height] duration-300 ease-in-out ${
//           expanded ? 'max-h-96' : 'max-h-0'
//         }`}
//       >
//         <div className="p-4">
//           <Timeline
//             items={
//               activity
//                 .map((a) => {
//                   const createTime = a.createTime?.toDate();
//                   const state = getTimelineInfo(a.type);
//                   if (!state) return null;
//                   return {
//                     dot: state.icon,
//                     // color: 'green',
//                     children: (
//                       <div>
//                         <div>{state.text}</div>
//                         <div className="text-xs text-muted-foreground">
//                           {t('date.exactFormat', {
//                             dayOfWeek: createTime,
//                             dayOfMonth: createTime,
//                             month: createTime,
//                             time: createTime,
//                           })}
//                         </div>
//                       </div>
//                     ),
//                   };
//                 })
//                 .filter((i) => i !== null) as any[]
//               // {
//               //   dot: pending,
//               //   // color: 'red',
//               //   children: <div>hehe</div>,
//               // },
//               // {
//               //   dot: onhold,
//               //   // color: 'default',
//               //   children: <div>hehe</div>,
//               // },
//             }
//           />
//         </div>
//       </div>
//     </div>
//   );
// }

// function getTimelineInfo(activityType: ActivityType): { icon: JSX.Element; text: string } | null {
//   switch (activityType) {
//     // const pending = getStatusIcon('deviation-pending');
//     // const onhold = getStatusIcon('deviation-onhold');
//     case ActivityType.ACTIVITY_INTERNAL_AUDIT_DEVIATIONS:
//       return {
//         icon: getStatusIcon('deviation'),
//         text: 'Afvigelse',
//       };
//     case ActivityType.ACTIVITY_INTERNAL_AUDIT_APPROVED:
//       return {
//         icon: getStatusIcon('deviation-resolved'),
//         text: 'Afvigelse løst',
//       };
//     case ActivityType.ACTIVITY_INTERNAL_AUDIT_ONHOLD:
//       return {
//         icon: getStatusIcon('deviation-onhold'),
//         text: 'På hold',
//       };
//     case ActivityType.ACTIVITY_INTERNAL_AUDIT_PENDING:
//       return {
//         icon: getStatusIcon('deviation-pending'),
//         text: 'Afventer godkendelse',
//       };
//     case ActivityType.ACTIVITY_INTERNAL_AUDIT_REJECTED:
//       return {
//         icon: getStatusIcon('deviation-rejected'),
//         text: 'Udbedring afvist',
//       };

//     default:
//       return null;
//   }
// }

// function ApproveActions({ auditId, subTaskId }: { auditId: string; subTaskId: string }) {
//   const [approveRequest, setApproveRequest] = useState(RequestState.INITIAL);
//   const [rejectRequest, setRejectRequest] = useState(RequestState.INITIAL);

//   const approveAudit = async () => {
//     try {
//       setApproveRequest(RequestState.PENDING);
//       // if (!selectedAuditDate) {
//       //   toast.error('Vælg en dato');
//       //   return;
//       // }

//       const { id } = await api.approveInternalAudit({
//         auditId,
//         subTaskId,
//       });
//       setApproveRequest(RequestState.RESOLVED);

//       toast.success('Auditering godkendt');
//     } catch (error) {
//       console.error(error);
//       toast.error('Der skete en fejl. Prøv igen senere.');
//       setApproveRequest(RequestState.REJECTED);
//     }
//   };

//   const rejectAudit = async () => {
//     try {
//       setRejectRequest(RequestState.PENDING);
//       // if (!selectedAuditDate) {
//       //   toast.error('Vælg en d<ato');
//       //   return;
//       // }

//       const { id } = await api.rejectInternalAudit({
//         auditId,
//         subTaskId,
//       });
//       setRejectRequest(RequestState.RESOLVED);

//       toast.success('Auditering godkendt');
//     } catch (error) {
//       console.error(error);
//       toast.error('Der skete en fejl. Prøv igen senere.');
//       setRejectRequest(RequestState.REJECTED);
//     }
//   };
//   return (
//     <div className="flex items-center space-x-2">
//       <Tooltip>
//         <TooltipContent>
//           <p className="text-sm">Afvis udbedring</p>
//         </TooltipContent>
//         <TooltipTrigger asChild>
//           <Button
//             variant="outline"
//             size="icon"
//             onClick={rejectAudit}
//             loading={rejectRequest === RequestState.PENDING}
//             disabled={approveRequest === RequestState.PENDING}
//           >
//             <Ban className="text-red-600" />
//           </Button>
//         </TooltipTrigger>
//       </Tooltip>
//       <Tooltip>
//         <TooltipContent>
//           <p className="text-sm">Godkend udbedring</p>
//         </TooltipContent>
//         <TooltipTrigger asChild>
//           <Button
//             variant="outline"
//             size="icon"
//             loading={approveRequest === RequestState.PENDING}
//             onClick={approveAudit}
//             disabled={rejectRequest === RequestState.PENDING}
//           >
//             <Check className="text-green-600" />
//           </Button>
//         </TooltipTrigger>
//       </Tooltip>
//     </div>
//   );
// }

function getStatusByImportance(statuses: string[] = []): string {
  if (statuses.includes('deviation-resolved')) return 'deviation-resolved';
  if (statuses.includes('deviation-pending')) return 'deviation-pending';
  if (statuses.includes('deviation-onhold')) return 'deviation-onhold';
  if (statuses.includes('deviation-rejected')) return 'deviation-rejected';
  if (statuses.includes('deviation')) return 'deviation';
  if (statuses.includes('observation')) return 'observation';
  if (statuses.includes('passed')) return 'passed';
  return 'not-audited';
}

type TaskMatrix = {
  [taskId: string]: {
    [locationId: string]: {
      auditId: string;
      completeTime?: Date;
      status: any[];
      followUpTasks: string[];
    };
  };
};

export function buildLatestTaskMatrix(
  audits: InternalAudit[],
  masterTasks: typeof masterTaskList
): TaskMatrix {
  const latestAudits: { [locationId: string]: InternalAudit } = {};

  audits.forEach((audit) => {
    if (!audit.completeTime) return;
    const locId = audit.location.id;
    if (
      !latestAudits[locId] ||
      audit.completeTime.toMillis() > latestAudits[locId].completeTime!.toMillis()
    ) {
      latestAudits[locId] = audit;
    }
  });

  const matrix: TaskMatrix = {};
  masterTasks.forEach((mt) => {
    matrix[mt.id] = {};
  });

  Object.values(latestAudits).forEach((audit) => {
    const locationId = audit.location.id;
    Object.values(audit.subTasksMap).forEach((subTask) => {
      const taskId = subTask.taskId;
      if (!matrix[taskId]) matrix[taskId] = {};
      if (!matrix[taskId][locationId]) {
        // @ts-ignore
        // console.log({ deviationState: audit.deviationState, id: subTask, map: audit.subTasksMap });
        matrix[taskId][locationId] = {
          auditId: audit.id,

          completeTime: audit.completeTime?.toDate(),
          status: [
            // @ts-ignore
            audit?.deviationState?.[subTask.id]?.status || subTask.status,
          ],
          followUpTasks: [],
        };
      } else {
        matrix[taskId][locationId].status.push(
          // @ts-ignore
          audit?.deviationState?.[subTask.id]?.status || subTask.status
        );
      }
    });
  });

  return matrix;
}
